import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, Typography, Result, Spin, Button } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import authService from '../../services/authService';
import './AuthPages.scss';

const { Text } = Typography;

const VerifyEmailPage = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const verifyEmail = async () => {
      if (!token) {
        setError(t('auth.verifyEmail.noToken'));
        setLoading(false);
        return;
      }

      try {
        await authService.verifyEmail(token);
        setSuccess(true);
      } catch (err) {
        setError(err.response?.data?.error || t('auth.verifyEmail.failed'));
      } finally {
        setLoading(false);
      }
    };

    verifyEmail();
  }, [token, t]);

  // Loading state
  if (loading) {
    return (
      <div className="auth-page">
        <div className="auth-container">
          <div className="auth-card-wrapper">
            <Card className="auth-card" style={{ textAlign: 'center', padding: '60px 0' }}>
              <Spin size="large" />
              <Text style={{ display: 'block', marginTop: 16 }}>{t('auth.verifyEmail.verifying')}</Text>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="auth-page">
        <div className="auth-container">
          <div className="auth-card-wrapper bounce-in">
            <Card className="auth-card">
              <Result
                icon={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
                status="success"
                title={t('auth.verifyEmail.successTitle')}
                subTitle={t('auth.verifyEmail.successSubtitle')}
                extra={[
                  <Link to="/login" key="login">
                    <Button type="primary" size="large">
                      {t('auth.register.goToLogin')}
                    </Button>
                  </Link>
                ]}
              />
            </Card>

            <div className="auth-decoration">
              <div className="paw-print">🐾</div>
              <div className="paw-print">🐾</div>
              <div className="paw-print">🐾</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  return (
    <div className="auth-page">
      <div className="auth-container">
        <div className="auth-card-wrapper bounce-in">
          <Card className="auth-card">
            <Result
              icon={<CloseCircleOutlined style={{ color: '#ff4d4f' }} />}
              status="error"
              title={t('auth.verifyEmail.failedTitle')}
              subTitle={error || t('auth.verifyEmail.failedSubtitle')}
              extra={[
                <Link to="/login" key="login">
                  <Button type="primary" size="large">
                    {t('auth.register.goToLogin')}
                  </Button>
                </Link>,
                <Link to="/register" key="register">
                  <Button size="large">
                    {t('auth.verifyEmail.createNewAccount')}
                  </Button>
                </Link>
              ]}
            />
          </Card>

          <div className="auth-decoration">
            <div className="paw-print">🐾</div>
            <div className="paw-print">🐾</div>
            <div className="paw-print">🐾</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmailPage;
